// The emailed 6-digit code sign-in, shared by the Client Portal (Session AH brand redesign; the magic link replaced by
// a 6-digit verification code by Session AHH) and, since Session ATA, the Subcontractor Portal. One component so the
// re-read clock behind Resend (Session AQF) and the anti-enumeration wording live in one place for both. Passwordless: the customer enters their email, we send a
// one-time 6-digit code, they type it here. The SEND reply is always the same whether or not the
// email is registered, so the page never reveals who has an account (see api.ts portal.requestCode
// and the server-side anti-enumeration reasoning in portal.routes.ts).
//
// Deep link: /portal/login?email=... (from the welcome email or an office-sent invite) pre-fills
// the email and jumps straight to the code-entry step WITHOUT auto-requesting a fresh code -- the
// code already sitting in that email is what the customer is meant to type. Auto-sending here would
// silently replace it (a code request always issues a NEW code), invalidating the one they are
// reading.
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../../lib/api";
import { PortalFooter } from "./PortalShell";

// The committed brand logo (public/brand/, Session ACE; never recoloured).
const LOGO_SRC = "/brand/ele-logo.png";

const RESEND_COOLDOWN_MS = 60 * 1000;

// The sign-in page defaults to a light canvas (Director, 5 Oct 2026) with a toggle to the dark one. The choice is a
// per-device convenience kept in localStorage; every read and write is guarded because storage can be blocked.
const THEME_KEY = "ele-signin-theme";
function readIsLight(): boolean {
  try {
    return localStorage.getItem(THEME_KEY) !== "dark";
  } catch {
    return true;
  }
}

export interface CodeSignInProps {
  // "Client Portal" or "Subcontractor Portal": the page's sub-heading.
  productName: string;
  // Where a successful sign-in lands.
  destination: string;
  // The two calls: the request resolves { ok: false, message } for a refusal the page should show.
  requestCode: (email: string) => Promise<{ ok: boolean; message?: string }>;
  verifyCode: (email: string, code: string) => Promise<unknown>;
  // The prefix of the two input ids ("portal" keeps the customer portal's own ids).
  idPrefix: string;
}

export function CodeSignIn({ productName, destination, requestCode, verifyCode, idPrefix }: CodeSignInProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [isLight, setIsLight] = useState(readIsLight);
  function toggleTheme() {
    const next = !isLight;
    setIsLight(next);
    try {
      localStorage.setItem(THEME_KEY, next ? "light" : "dark");
    } catch {
      /* storage blocked: the choice simply lasts for this visit */
    }
  }

  const deepLinkEmail = params.get("email") ?? "";
  const [step, setStep] = useState<"email" | "code">(deepLinkEmail ? "code" : "email");
  const [email, setEmail] = useState(deepLinkEmail);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  // Deep-link entry never auto-sends, so there is nothing to be "resent" yet; the cooldown starts
  // only once THIS session has actually asked the server to send a code.
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Arm the resend cooldown from ONE clock read (Session AQF). Reading the clock here rather than
  // leaning on the `now` captured at mount matters: a customer who spent half a minute typing their
  // address was told to wait 90 seconds, because the 30 seconds of typing sat between the two reads.
  function armResendCooldown() {
    const t = Date.now();
    setNow(t);
    setResendAt(t + RESEND_COOLDOWN_MS);
  }

  // Tick once a second while a resend cooldown is active, purely to re-render the countdown. Two
  // things this deliberately does NOT do (Session AQF, each pinned by a test):
  //  - It never counts ticks. It re-reads the clock, and it re-reads it again whenever the customer
  //    comes back to the page, because a hidden tab's timers are throttled or suspended (Chrome
  //    clamps them to about once a minute, a backgrounded phone stops them). This screen is exactly
  //    where a customer leaves to fetch the code out of their email, and before this they could come
  //    back minutes later to a Resend button still counting down and still refusing to be pressed.
  //  - It does not keep ticking once the cooldown is over. There is then nothing to count, so the
  //    page must stop re-rendering itself once a second for the rest of the visit.
  useEffect(() => {
    if (resendAt == null) return;
    const sync = () => setNow(Date.now());
    const id = setInterval(() => {
      sync();
      if (Date.now() >= resendAt) clearInterval(id);
    }, 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [resendAt]);

  const resendReady = resendAt == null || now >= resendAt;
  const resendSecondsLeft = resendAt == null ? 0 : Math.max(0, Math.ceil((resendAt - now) / 1000));

  async function sendCode(targetEmail: string) {
    setBusy(true);
    setConnectionError(false);
    setSendError(null);
    try {
      const res = await requestCode(targetEmail.trim());
      if (res.ok === false) {
        setSendError(res.message ?? "We could not send the verification code right now. Please call the office on 0203 576 4760.");
      } else {
        setStep("code");
        armResendCooldown();
      }
    } catch (err) {
      // A real response from the server (any status) still means the request was received: the
      // endpoint's own contract always reports success, so the page never leaks whether the email
      // is registered. A request that never reached the server at all (offline, DNS, CORS) is a
      // genuinely different failure and gets told the truth instead.
      if (err instanceof ApiError) {
        setStep("code");
        armResendCooldown();
      } else {
        setConnectionError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    await sendCode(email);
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setConnectionError(false);
    setVerifyError(null);
    try {
      await verifyCode(email.trim(), code.trim());
      navigate(destination, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setVerifyError(err.message || "Incorrect or expired code.");
      } else {
        setConnectionError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!resendReady || busy) return;
    setVerifyError(null);
    await sendCode(email);
  }

  return (
    <div className={isLight ? "portal-ui flex min-h-dvh flex-col bg-white text-[#1a1a1a]" : "portal-ui flex min-h-dvh flex-col bg-ele-bg text-ele-body"}>
      {/* Brand accent bar (Session AEH: the tri-colour gradient signature shared with the
          rest of the Client Portal, PortalShell.tsx, instead of a plain purple line). */}
      {/* The same dark brand band as the quote page and the signed-in portal (Director, 5 Oct 2026):
          logo and product name left, phone and email right, the theme toggle underneath them. */}
      <header
        className="px-4 py-4 md:px-8"
        style={{ background: "linear-gradient(90deg, #050816 0%, #1a0a33 55%, #2b0f4f 100%)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <img src={LOGO_SRC} alt="East London Elevators" className="h-9 w-auto self-start" />
            <span className="font-menu text-[11px] font-bold uppercase tracking-[0.2em] text-[#63C41F]">{productName}</span>
          </div>
          <div className="flex flex-col items-end gap-1 text-right font-body text-xs text-[#CACACA]">
            <a href="tel:02035764760" className="text-sm font-bold text-[#2CB9F3]">0203 576 4760</a>
            <a href="mailto:info@eastlondonelevators.co.uk" className="hidden text-[#CACACA] sm:inline">info@eastlondonelevators.co.uk</a>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={isLight ? "Switch to dark theme" : "Switch to light theme"}
              className="mt-1 rounded-ele border border-white/30 px-3 py-1 text-xs text-[#CACACA] hover:text-white"
            >
              {isLight ? "Dark theme" : "Light theme"}
            </button>
          </div>
        </div>
      </header>
      <div className="ele-fade-in mx-auto flex w-full max-w-md flex-1 flex-col px-4 py-10">
        <div className="text-center">
          <h1 className={isLight ? "font-heading text-3xl font-bold tracking-wide text-[#050816]" : "font-heading text-3xl font-bold tracking-wide text-ele-white"}>
            Sign in to your {productName}
          </h1>
          <hr aria-hidden="true" className="mx-auto mt-4 w-24 border-0 border-t-2 border-ele-divider" />
        </div>

        <main className="mt-8">
          {connectionError && (
            <p role="alert" className={isLight ? "mb-4 rounded-ele border border-[#c0392b] bg-[#f7f7f9] px-3 py-2 text-center text-sm text-[#050816]" : "mb-4 rounded-ele border border-ele-danger bg-[rgba(255,255,255,0.04)] px-3 py-2 text-center text-sm text-ele-white"}>
              We could not reach the server. Please check your connection and try again, or call the office on 0203 576 4760.
            </p>
          )}

          {step === "email" ? (
            <form onSubmit={submitEmail}>
              {sendError && (
                <p role="alert" className={isLight ? "mb-4 rounded-ele border border-[#c0392b] bg-[#f7f7f9] px-3 py-2 text-center text-sm text-[#050816]" : "mb-4 rounded-ele border border-ele-danger bg-[rgba(255,255,255,0.04)] px-3 py-2 text-center text-sm text-ele-white"}>
                  {sendError}
                </p>
              )}
              <label className={isLight ? "mb-1 block font-menu text-xs uppercase tracking-wide text-[#4b5563]" : "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body"} htmlFor={`${idPrefix}-email`}>
                Email address
              </label>
              <input
                id={`${idPrefix}-email`}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={isLight ? "w-full rounded-ele border border-ele-purple bg-white px-3 py-2.5 text-[#050816] outline-none focus:border-ele-green" : "w-full rounded-ele border border-ele-purple bg-black/30 px-3 py-2.5 text-ele-white outline-none focus:border-ele-green"}
              />
              <button
                type="submit"
                disabled={busy}
                className="mt-5 w-full rounded-ele bg-ele-green px-4 py-3 font-heading tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
              >
                {busy ? "Sending..." : "Send verification code"}
              </button>
              <p className={isLight ? "mt-4 text-center text-sm text-[#4b5563]" : "mt-4 text-center text-sm text-ele-body"}>
                Enter your email address and we will send you a 6-digit verification code. No password required.
              </p>
            </form>
          ) : (
            <form onSubmit={submitCode}>
              <div className={isLight ? "mb-4 rounded-ele border border-ele-green bg-[#f7f7f9] p-4 text-center" : "mb-4 rounded-ele border border-ele-green bg-[rgba(255,255,255,0.04)] p-4 text-center"}>
                <p className={isLight ? "text-[#4b5563]" : "text-ele-body"}>
                  If <strong className={isLight ? "text-[#050816]" : "text-ele-white"}>{email}</strong> is on our system, we have sent a 6-digit
                  verification code. It can be used once and is valid for 24 hours.
                </p>
              </div>
              {verifyError && (
                <p role="alert" className={isLight ? "mb-4 rounded-ele border border-[#c0392b] bg-[#f7f7f9] px-3 py-2 text-center text-sm text-[#050816]" : "mb-4 rounded-ele border border-ele-danger bg-[rgba(255,255,255,0.04)] px-3 py-2 text-center text-sm text-ele-white"}>
                  {verifyError}
                </p>
              )}
              <label className={isLight ? "mb-1 block font-menu text-xs uppercase tracking-wide text-[#4b5563]" : "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body"} htmlFor={`${idPrefix}-code`}>
                Verification code
              </label>
              <input
                id={`${idPrefix}-code`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                className={isLight ? "w-full rounded-ele border border-ele-purple bg-white px-3 py-2.5 text-center text-2xl tracking-[0.4em] text-[#050816] outline-none focus:border-ele-green" : "w-full rounded-ele border border-ele-purple bg-black/30 px-3 py-2.5 text-center text-2xl tracking-[0.4em] text-ele-white outline-none focus:border-ele-green"}
                placeholder="000000"
              />
              <button
                type="submit"
                disabled={busy || code.length === 0}
                className="mt-5 w-full rounded-ele bg-ele-green px-4 py-3 font-heading tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
              >
                {busy ? "Checking..." : "Sign in"}
              </button>
              <div className="mt-4 flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setVerifyError(null);
                  }}
                  className={isLight ? "text-[#4b5563] underline hover:text-[#050816]" : "text-ele-body underline hover:text-ele-white"}
                >
                  Not you? Change email
                </button>
                <button
                  type="button"
                  disabled={!resendReady || busy}
                  onClick={resend}
                  className={isLight ? "text-[#0e74a6] underline disabled:cursor-not-allowed disabled:text-[#6b6b6b] disabled:opacity-60 disabled:no-underline" : "text-ele-loler underline disabled:cursor-not-allowed disabled:text-ele-body disabled:opacity-60 disabled:no-underline"}
                >
                  {resendReady ? "Resend code" : `Resend code (${resendSecondsLeft}s)`}
                </button>
              </div>
            </form>
          )}
        </main>

        <PortalFooter light={isLight} />
      </div>
    </div>
  );
}
